import { useQuery } from '@tanstack/react-query';
import { Paintbrush } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import Badge from '@/components/ui/Badge';
import api from '@/lib/api';
import type { BrandKitSummary } from '@/components/admin/branding/types';

const STATUS_LABEL: Record<string, { label: string; variant: 'gray' | 'blue' | 'green' | 'red' }> = {
  draft: { label: 'Draft', variant: 'gray' },
  packaging: { label: 'Generating…', variant: 'blue' },
  ready: { label: 'Ready', variant: 'green' },
  failed: { label: 'Failed', variant: 'red' },
};

/** Latest brand kit at a glance, or the launch CTA when there are none. */
export default function BrandKitWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'branding'],
    queryFn: () => api.get<{ kits: BrandKitSummary[] }>('/admin/branding/kits'),
    staleTime: 60000,
  });

  const latest = data?.kits[0];
  const applied = data?.kits.find((k) => k.applied_at);

  return (
    <WidgetShell
      title="Brand kit"
      icon={<Paintbrush />}
      to="/admin/settings/branding"
      isLoading={isLoading}
      error={error}
      skeletonClassName="h-20"
    >
      {data &&
        (latest ? (
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-ink">{latest.name}</span>
              <Badge variant={(STATUS_LABEL[latest.status] ?? STATUS_LABEL.draft).variant}>
                {(STATUS_LABEL[latest.status] ?? STATUS_LABEL.draft).label}
              </Badge>
            </div>
            <p className="text-xs text-ink-muted">
              {applied
                ? `“${applied.name}” is applied to your storefront.`
                : 'No kit applied to the storefront yet.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-ink-soft">
            Generate a full brand identity — logo, colors, fonts and marketing files — in the
            Branding Studio.
          </p>
        ))}
    </WidgetShell>
  );
}
